import { Dayjs } from "dayjs";
import * as Yup from "yup";
import { AsyncSelectValue } from "../components/AsyncSelect";
import { MData } from "../components/mask-multi-select/types";
import { DEAInformation, ProviderRoleTypeEnum } from "../services";
import { REQUIRED_ADDRESS_SCHEMA } from "./ProviderGroupFormInitialValues";

/** One licence row on the provider-user form; the picker holds a Date, the API a string. */
export interface ProviderLicenseInformation {
  uuid?: string;
  licenseState?: string;
  licenseNumber?: string;
  licenseName?: string;
  expiryDate?: Date | string;
  licenseActiveDate?: Date | string;
}

export type ProviderUserType = {
  uuid?: string;
  federalTaxIdNumber?: string;
  roleType?: ProviderRoleTypeEnum;
  active?: boolean;
  admin_access?: boolean;
  status?: boolean;
  timezone?: string;
  avatar?: string;
  providerType?: string;
  role?: string;
  /** Supervising MD who co-signs this provider's notes; sent as `cosignerUuid`. */
  coSigner?: AsyncSelectValue | null;
  /** uuid → name of the assigned co-signer, as the provider API returns it. */
  cosigner?: Record<string, string>;
  firstName: string;
  lastName: string;
  gender: string;
  /** The picker holds a Dayjs; the API sends and receives an ISO string. */
  birthDate?: Dayjs | string;
  phone?: string;
  /** The input holds a string; the API model declares a number. */
  npi?: string | number;
  doesNotHaveNpi?: boolean;
  specialities?: MData[];
  /** The input holds a string; the API model declares a number. */
  groupNpiNumber?: string | number;
  licensedStates?: MData[];
  licenseNumber?: string;
  acceptedInsurances?: MData[];
  experience?: string;
  taxonomyNumber?: string;
  workLocations?: MData[];
  email?: string;
  officeFaxNumber?: string;
  areaFocus?: string;
  hospitalAffiliation?: string;
  ageGroupSeen?: MData[];
  spokenLanguages?: MData[];
  providerEmployment?: string;
  acceptNewPatient?: boolean;
  acceptCash?: boolean;
  insurance_verification?: string;
  prior_authorization?: string;
  secondOpinion?: string;
  /** Option list on every form; the demo edit constant still seeds a single legacy string. */
  careService?: MData[] | string;
  bio?: string;
  expertise?: string;
  workExperience?: string;
  licenceInformation: ProviderLicenseInformation[];
  deaInformation?: DEAInformation[];
  practiceName?: string;
  electronicSignature?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    country?: string;
    zipcode?: string;
  };
};

export const PROVIDER_USER_VALIDATION_SCHEMA = Yup.object({
  phone: Yup.string().min(12, "Contact Number must be at least 8 digits"),
  federalTaxIdNumber: Yup.string()
    .required()
    .label("Federal Tax ID Number")
    .test(
      "federal-tax-id-number-length",
      "Federal Tax ID Number must be exactly 9 digits",
      (value) => !value || /^\d{9}$/.test(value)
    ),
  // avatar: Yup.string(),
  // provider_type: Yup.string(),
  role: Yup.string().required().label("Role"),
  npi: Yup.string().when("doesNotHaveNpi", {
    is: true,
    then: (schema) => schema.nullable(),
    otherwise: (schema) => schema.required().label("NPI Number"),
  }),
  firstName: Yup.string().required().label("First Name"),
  lastName: Yup.string().required().label("Last Name"),
  gender: Yup.string().required().label("Gender"),
  // phone: Yup.string(),
  // npi_number: Yup.string(),
  specialities: Yup.array().label("Speciality"),
  // licensedStates: Yup.array().of(Yup.string()),
  // groupNpiNumber: Yup.string(),
  // licenseNumber: Yup.string(),
  // insurance_accepted: Yup.array().of(Yup.string()),
  // year_of_exp: Yup.string(),
  // taxonomyNumber: Yup.string(),
  // workLocations: Yup.array().of(Yup.string()),
  email: Yup.string()
    .email()
    .matches(/.*@.+\..+/, "Please Enter a valid Email")
    .label("Email")
    .required()
    .label("Email"),
  // fax: Yup.string(),
  // area_of_focus: Yup.string(),
  // hospitalAffiliation: Yup.string(),
  // age_group_seen: Yup.array().of(Yup.string()),
  // language_spoken: Yup.array().of(Yup.string()),
  // employment_referral_number: Yup.string(),
  // new_patient: Yup.string(),
  // cash_pay: Yup.string(),
  // insurance_verification: Yup.string(),
  // prior_authorization: Yup.string(),
  // second_opinion: Yup.string(),
  // speciality_care_service: Yup.string(),
  // bio: Yup.string(),
  // expertise_in: Yup.string(),
  // workExperience: Yup.string(),
  address: REQUIRED_ADDRESS_SCHEMA.required(),
  licenceInformation: Yup.array().of(
    Yup.object().shape({
      licenseActiveDate: Yup.date()
        .nullable()
        .test(
          "active-before-expiry",
          "Active Date cannot be later than Expiry Date",
          function (value) {
            const { expiryDate } = this.parent;
            if (!value || !expiryDate) return true;
            return new Date(value) <= new Date(expiryDate);
          }
        ),
    })
  ),
  deaInformation: Yup.array().of(
    Yup.object().shape({
      deaActiveDate: Yup.date()
        .nullable()
        .test(
          "active-before-term",
          "Active Date cannot be later than Term Date",
          function (value) {
            const { deaTermDate } = this.parent;
            if (!value || !deaTermDate) return true;
            return new Date(value) <= new Date(deaTermDate);
          }
        ),
    })
  ),
  // .test("unique-ccm-pcm", function (value, { createError }) {
  //   const enrollments = value || [];
  //   const hasCCM = enrollments.some((el) => el.category === "ccm");
  //   const hasPCM = enrollments.some((el) => el.category === "pcm");

  //   if (hasCCM && hasPCM) {
  //     /** Collect all the error of position for the path
  //      * this has to do because ccm and pcm may be at any position
  //      */
  //     const errors = enrollments?.reduce((acc, enrollment, ind) => {
  //       const isInclude =
  //         enrollment.category === "ccm" || enrollment.category === "pcm";
  //       /** Collect all the error with path
  //        */
  //       if (isInclude)
  //         acc.push(
  //           createError({
  //             path: `enrollments[${ind}].category`,
  //             message: "Patient can’t enrolled in both programs-CCM&PCM",
  //           })
  //         );
  //       return acc;
  //     }, [] as Yup.ValidationError[]);

  //     /** Throw all collected Yup validation Error
  //      */
  //     throw new Yup.ValidationError(errors);
  //   }

  //   return true;
  // }),
});

export const PROVIDER_USER_INITIAL_VALUES: ProviderUserType = {
  federalTaxIdNumber: "",
  roleType: "PROVIDER",
  active: false,
  admin_access: true,
  status: false,
  timezone: undefined,
  avatar: "",
  providerType: undefined,
  role: "",
  firstName: "",
  lastName: "",
  gender: "",
  birthDate: undefined,
  phone: "",
  npi: "",
  doesNotHaveNpi: false,
  specialities: undefined,
  groupNpiNumber: "",
  licensedStates: [],
  licenseNumber: "",
  acceptedInsurances: [],
  experience: "",
  taxonomyNumber: "",
  workLocations: [],
  email: "",
  officeFaxNumber: "",
  areaFocus: "",
  hospitalAffiliation: "",
  ageGroupSeen: undefined,
  spokenLanguages: undefined,
  providerEmployment: "",
  acceptNewPatient: undefined,
  acceptCash: undefined,
  insurance_verification: "",
  prior_authorization: "",
  secondOpinion: "",
  careService: undefined,
  bio: "",
  expertise: "",
  workExperience: "",
  licenceInformation: [
    {
      uuid: "",
      licenseState: "",
      licenseNumber: "",
      licenseName: "",
      expiryDate: undefined,
    },
  ],
  deaInformation: [
    {
      deaState: "",
      deaNumber: "",
      deaTermDate: "",
      deaActiveDate: "",
    },
  ],
  practiceName: undefined,

  electronicSignature: undefined,
  address: {
    line1: "",
    line2: "",
    city: "",
    state: "",
    country: "",
    zipcode: "",
  },
};

export const PROVIDER_USER_EDIT_INITIAL_VALUES: ProviderUserType = {
  spokenLanguages: undefined,
  roleType: "PROVIDER",
  active: false,
  admin_access: true,
  status: true,
  timezone: "US/Alaska",
  avatar: "../../file.jpg",
  providerType: "MD",
  role: "specialist",
  firstName: "Corina",
  lastName: "James",
  gender: "female",
  birthDate: undefined,
  phone: "(569) 822-4144",
  npi: "(569) 822-4144",
  specialities: [],
  groupNpiNumber: "3456876546",
  licensedStates: [],
  licenseNumber: "98978t873283",
  acceptedInsurances: [],
  taxonomyNumber: "207X36587X",
  workLocations: [],
  email: "hennawest@gmail.com",
  officeFaxNumber: "0897 234567",
  areaFocus: "MD",
  hospitalAffiliation: "Alex Smith",
  providerEmployment: "123",
  acceptCash: false,
  insurance_verification: "email_me",
  prior_authorization: "enroll_me",
  secondOpinion: "email_me",
  careService: "don`t_enroll_me",
  bio: "A Board Certified Family Doctor with seven years of clinical experience, specializing in patient care, case management, family medicine, and communication. Adept at building positive relationships with patients through the delivery of compassionate and empathetic care.",
  workExperience:
    " General Practitioner I Kaiser Permanent - San Jose, CA \n  Medical Resident I Sacred Heart Hospital - Sacramento, CA",
  licenceInformation: [
    {
      uuid: "",
      licenseState: "",
      licenseNumber: "",
      licenseName: "",
      expiryDate: new Date(),
    },
  ],

  electronicSignature: undefined,
  address: {
    line1: "",
    line2: "",
    city: "",
    state: "",
    country: "",
    zipcode: "",
  },
};
